import * as React from "react";

import { PageAlternate, BreadCrumbProps } from "../../subs/PageAlternate";

import { Typography } from "@mui/material";
import { useParams } from "react-router";
import { UserContext } from "../../UserProvider";
import { AlertProps } from '@mui/material/Alert';

import { WSConfigTreeData, getWorkshopConfigTree } from "../../types/WSConfigData";
import { httpGet } from "../../Web";
import { TestingInfo } from "./../WSTesting/TestingInfo";
import { WSConfigProductData, WSConfigTreeKey, getconfigproduct, GetEmptyTestingKey } from '../../types/WSConfigData';
import { WSConfigServer } from "./WSConfigServer";
import { getWorkshopUsers } from '../../types/WorkshopUser';
import { isGlobalAdmin } from '../../types/User';

import { WSConfigProdTree } from "./WSConfigProdTree";

import { useSnackbar } from '../../../contexts/SnackBarContext';

export const WSTestingConfig = ({ disableForms }: { disableForms?: boolean }) => {
    const { id } = useParams();
    const user = React.useContext(UserContext);
    const { showSnackbar } = useSnackbar();

    const [configTree, setConfigTree] = React.useState<WSConfigTreeData[]>([]);
    const [wsConfigProductData, setWsConfigProductData] = React.useState<WSConfigProductData>();
    const [wsConfigProductKey, setWsConfigProductKey] = React.useState<WSConfigTreeKey>(GetEmptyTestingKey());
    const [pageHeading, setPageHeading] = React.useState<string>('OPC Interoperability Test Page');
    const [selectedNode, setSelectedNode] = React.useState<string>('none');
    const [isAdminUser, setIsAdminUser] = React.useState<boolean>(false);

    const showUserFeedback = React.useCallback((msg: string | null, severity: AlertProps['severity'] = 'error') => {
        msg && showSnackbar(msg, severity);
    }, [showSnackbar]);

    const refreshTreeData = async () => {
        const configTreeResponse = await getWorkshopConfigTree(Number(id), new AbortController());

        if (configTreeResponse.failed) {
            showUserFeedback(configTreeResponse.errorText);
            return;
        }

        setConfigTree(configTreeResponse.result);
    };

    React.useEffect(() => {
        console.log(`initializing user account check`);
        const interval = setInterval(() => {
            httpGet('/api/account/checkloginstatus', new AbortController());
        }, 60000);

        return () => {
            console.log(`clearing user account check Timer`);
            clearInterval(interval);
        };
    }, []);

    React.useEffect(() => {
        console.log(`initializing user account check`);
        const interval = setInterval(() => {
            refreshTreeData();
        }, 120000);

        return () => {
            console.log(`clearing user account check Timer`);
            clearInterval(interval);
        };
    });

    React.useEffect(() => {
        if (wsConfigProductData === undefined) {
            const heading = "OPC Interoperability Test Page";
            setPageHeading(heading);
        }
        else {
            setPageHeading(`${wsConfigProductData.productName} ${wsConfigProductData.productVersion}`);
        }
    }, [wsConfigProductData]);

    React.useEffect(() => {
        const fetch = async () => {
            try {

                let globalAdminFlag = await isGlobalAdmin(user.user.id!!, new AbortController());
                if (!globalAdminFlag) {
                    const workshopUsersResponse = await getWorkshopUsers(Number(id), new AbortController());
                    if (!workshopUsersResponse.failed) {
                        globalAdminFlag = workshopUsersResponse.result.find(wu => wu.userId === user.user.id)?.isWorkshopAdmin ?? false;
                    }
                }
                setIsAdminUser(globalAdminFlag);

                await refreshTreeData();

            } catch (error) {
                showUserFeedback(String(error));
            }
        }

        fetch();
    }, [id, user]);

    const refreshProductData = (tcCount?: number) => {
        if (wsConfigProductKey.workshopProductId !== 0) {
            let wsConfigProductDataLoc = wsConfigProductData;
            if (tcCount === 0 || tcCount === undefined) {
                getconfigproduct(selectedNode, new AbortController()).then(response => {
                    if (response.failed) {
                        showUserFeedback(response.errorText);
                        return;
                    }
                    wsConfigProductDataLoc = response.result;
                    setWsConfigProductData(response.result);

                    if (tcCount === 0) {
                        if (!wsConfigProductDataLoc) return;

                        let configTreeLoc = [...configTree];
                        const combinationData = wsConfigProductDataLoc.combinationData.find(cd => cd.testCombinationId === wsConfigProductKey.testCombinationId);

                        if (combinationData) {
                            if (combinationData.isProvider) {
                                let nodeP = configTreeLoc.find(ct => ct.key.testCombinationId === wsConfigProductKey.testCombinationId
                                    && ct.key.wsTreeEntryType === 1 && ct.key.workshopProductId === wsConfigProductKey.workshopProductId);
                                if (nodeP) nodeP.alternateIcon = combinationData.providerReadyToTest;        // update (product) provider node with ready to test status
                            }
                            if (combinationData.isConsumer) {
                                let nodeC = configTreeLoc.find(ct => ct.key.testCombinationId === wsConfigProductKey.testCombinationId
                                    && ct.key.wsTreeEntryType === 2 && ct.key.workshopProductId === wsConfigProductKey.workshopProductId);
                                if (nodeC) nodeC.alternateIcon = combinationData.consumerReadyToTest;        // update (product) consumer node with ready to test status
                            }
                        }
                        setConfigTree(configTreeLoc);
                    }
                }).catch(error => {
                    showUserFeedback(String(error));
                });
            }

            if (tcCount !== undefined) {
                let configTreeLoc = [...configTree];

                let nodeP = configTreeLoc.find(ct => ct.key.testCombinationId === wsConfigProductKey.testCombinationId
                    && ct.key.wsTreeEntryType === 1 && ct.key.workshopProductId === wsConfigProductKey.workshopProductId);
                let nodeC = configTreeLoc.find(ct => ct.key.testCombinationId === wsConfigProductKey.testCombinationId
                    && ct.key.wsTreeEntryType === 2 && ct.key.workshopProductId === wsConfigProductKey.workshopProductId);
                let nodePT = configTreeLoc.find(ct => ct.key.testCombinationId === wsConfigProductKey.testCombinationId
                    && ct.key.wsTreeEntryType === 1 && ct.key.workshopProductId === 0);
                let nodeCT = configTreeLoc.find(ct => ct.key.testCombinationId === wsConfigProductKey.testCombinationId
                    && ct.key.wsTreeEntryType === 2 && ct.key.workshopProductId === 0);
                let nodeTC = configTreeLoc.find(ct => ct.key.testCombinationId === wsConfigProductKey.testCombinationId
                    && ct.key.wsTreeEntryType === 0 && ct.key.workshopProductId === 0);

                if (nodeP || nodeC) {
                    if (nodeP) nodeP.testCasesMarked += tcCount;        // update (product) provider node with marked test cases count
                    if (nodeC) nodeC.testCasesMarked += tcCount;        // update (product) consumer node with marked test cases count
                    if (nodePT) nodePT.testCasesMarked += tcCount;      // update provider (name) template node with marked test cases count
                    if (nodeCT) nodeCT.testCasesMarked += tcCount;      // update consumer (name) template node with marked test cases count
                    if (nodeTC) nodeTC.testCasesMarked += tcCount;      // update test combination node with marked test cases count
                    setConfigTree(configTreeLoc);
                }
            }
        }
    };

    React.useEffect(() => {
        refreshProductData();
    }, [selectedNode]);

    const handleNodeSelect = (event: React.SyntheticEvent, nodeIds: string) => {
        let tComboId: string, tComboType: string, wsProdId: string;
        // nodeIds is in the format "combination-treeType-wsprodId", when the lower product is selected
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        [tComboId, tComboType, wsProdId] = nodeIds.split('-');

        if (wsProdId === undefined) {
            const heading = "OPC Interoperability Test Page"
            setWsConfigProductKey(GetEmptyTestingKey());
            setPageHeading(heading);
        }
        else {
            console.log('nodeIds: ', nodeIds);
            setWsConfigProductKey({
                workshopProductId: Number(wsProdId),
                wsTreeEntryType: Number(tComboType),
                testCombinationId: Number(tComboId)
            });
        }
        setSelectedNode(nodeIds);
    }

    const breadcrumbs: BreadCrumbProps[] = [
        { to: "/TestingTab", text: "Workshop Selection" },
        { to: `/WSTesting/${id}`, text: "Configure (Legacy)" },
        { to: `/wstestingrecords/${id}`, text: "Start Testing" },
    ];

    return <PageAlternate
        sidePanel={<WSConfigProdTree wsConfigTreeData={configTree} getSelectedNode={() => selectedNode} nodeSelectHandler={handleNodeSelect} />}
        backButton heading={<Typography color={'info'} variant="h4">{isAdminUser ? pageHeading + ' - Admin View' : pageHeading}</Typography>}
        breadcrumbs={breadcrumbs}>
        {[
            ( (wsConfigProductData && wsConfigProductData?.key.wsTreeEntryType !== 0) ?
                <WSConfigServer wsConfigProductData={wsConfigProductData} setWsConfigProductData={setWsConfigProductData} disabled={false}
                    refreshProductData={refreshProductData} isGlobalAdminUser={isAdminUser} />
                : 
                < TestingInfo />
            )
        ]}
    </PageAlternate>
}